import { Injectable } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { compact, every, flatten, isEmpty, trim } from 'lodash';
import { isString } from 'util';
import { ErrorService } from '../../../error-reporting/error.service';

export const EMAIL_MAX_LENGTH = 250;
export const EMAIL_MAX_LENGTH_MULTIPLE = 2500;
// paypal regex https://invoice2go.atlassian.net/browse/I2G-3803
export const EMAIL_REGEX = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
export const EMAIL_REGEX_SEPARATOR = /[\s,;]+/;
export const EMAIL_SEPARATOR = ', ';

@Injectable()
export class EmailService {
  constructor(private _errorService: ErrorService) {}

  tokenizeEmails(emails: string, emptyValue: string[] = []): string[] {
    if (!emails) {
      return emptyValue;
    } else if (!isString(emails)) {
      this._errorService.alertSystem(
        new Error('tried to tokenize a non-string email'),
        {
          emails,
          emailsTypeOf: typeof emails,
        }
      );

      return emptyValue;
    }

    const tokenizedEmails = compact(emails.split(EMAIL_REGEX_SEPARATOR));

    return tokenizedEmails.length > 0 ? tokenizedEmails : emptyValue;
  }

  concatenateEmails(emails: string[]) {
    // parse emails to support old email versions
    const flattenEmails = flatten(
      emails
        .filter((x) => !!x)
        .map((email) => email.split(EMAIL_REGEX_SEPARATOR))
    );

    return flattenEmails.filter((x) => !!x).join(EMAIL_SEPARATOR);
  }

  validateEmails(emails: string, allowEmpty: boolean = false): boolean {
    const splitEmails = this.tokenizeEmails(emails);

    if (!allowEmpty && isEmpty(splitEmails)) {
      return false;
    }

    return every(splitEmails, (email) => this.isValidEmail(email));
  }

  isValidEmail(email: string, allowEmpty: boolean = false): boolean {
    if (!allowEmpty && isEmpty(email)) {
      return false;
    }

    return !!trim(email).match(EMAIL_REGEX);
  }

  emailValidatorFn(allowMultiple: boolean = true, allowEmpty: boolean = true) {
    return (formControl: FormControl): any => {
      const isValid = allowMultiple
        ? this.validateEmails(formControl.value, allowEmpty)
        : this.isValidEmail(formControl.value, allowEmpty);

      return isValid ? null : { email: { valid: false } };
    };
  }

  emailArrayValidatorFn() {
    return (formArray: FormArray): any => {
      const concatenatedEmails = this.concatenateEmails(
        formArray.value as string[]
      );

      return concatenatedEmails.length > EMAIL_MAX_LENGTH_MULTIPLE
        ? { maxlength: { valid: false } }
        : null;
    };
  }
}
